// Dynamic image loader for location-specific images
// This allows for easy addition of new locations and images

/**
 * Preload a single image so the browser fetches it early (e.g. first hero image).
 * Call from useEffect and clean up on unmount.
 * @param {string} src - Image URL
 * @returns {() => void} Cleanup function to remove the preload link
 */
export const preloadImage = (src) => {
  if (!src || typeof src !== "string") return () => {};
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = src;
  document.head.appendChild(link);
  return () => {
    try {
      document.head.removeChild(link);
    } catch (_) {}
  };
};

// Fallback images array (will be populated dynamically)

// Function to dynamically load images for a specific location
export const loadLocationImages = async (location) => {
  try {
    // Try to dynamically import images from the location folder
    const locationImages = await import(`../assets/${location}/index.js`);
    return locationImages.default || locationImages;
  } catch (error) {
    console.warn(`Could not load images for location: ${location}`, error);
    // Try to get fallback from srinagar if available
    try {
      const srinagarImages = await import(`../assets/srinagar/index.js`);
      return srinagarImages.default || srinagarImages;
    } catch (fallbackError) {
      console.warn("No fallback images available");
      return [];
    }
  }
};

// Function to get images synchronously (for immediate use)
export const getLocationImages = (location) => {
  try {
    // Try to dynamically import images from the location folder
    const locationImages = require(`../assets/${location}/index.js`);
    return locationImages.default || locationImages;
  } catch (error) {
    console.warn(`Could not load images for location: ${location}`, error);
    // Try to get fallback from srinagar if available
    try {
      const srinagarImages = require(`../assets/srinagar/index.js`);
      return srinagarImages.default || srinagarImages;
    } catch (fallbackError) {
      console.warn("No fallback images available");
      // Return empty array as last resort
      return [];
    }
  }
};

// Function to automatically discover all available locations
export const getAvailableLocations = () => {
  try {
    // This will work if you have a locations config file
    const { LOCATIONS } = require("../config/locations");
    return Object.keys(LOCATIONS);
  } catch (error) {
    console.warn("Could not load locations config:", error);
    return ["srinagar", "baramulla", "jammu"]; // fallback
  }
};

// Function to preload all location images
export const preloadAllLocationImages = async () => {
  const locations = getAvailableLocations();
  const promises = locations.map((location) => loadLocationImages(location));

  try {
    await Promise.all(promises);
  } catch (error) {
    console.warn("Some location images failed to preload:", error);
  }
};

// Function to create a generic index.js file for any location
export const createLocationIndexTemplate = (locationName) => {
  return `// ${locationName} location images
// Automatically import all images from the ${locationName} folder
const ${locationName}Images = require.context('./', false, /(jpg|jpeg|png|gif|webp)$/);

// Convert to array and sort by filename
const images = ${locationName}Images.keys()
  .map(key => ${locationName}Images(key))
  .sort((a, b) => {
    // Extract filename without extension for sorting
    const nameA = a.split('/').pop().split('.')[0];
    const nameB = b.split('/').pop().split('.')[0];
    return nameA.localeCompare(nameB, undefined, { numeric: true });
  });

export default images;
`;
};
